import { useState } from 'react';
import { Button, Field, Input, Select, Stack, Text } from './ui';
import { COUNTRIES } from '../lib/countries';
import type { ShippingAddress } from '../lib/queries';

/**
 * Reusable delivery-address form built on the design-system primitives. Used by
 * the profile Address book (add / edit) and anywhere else a member enters a
 * shipping address. Owns its own validation + busy/error so callers just pass
 * an async onSubmit.
 */

// Carriers reject these countries' addresses without a state / province.
const STATE_REQUIRED = new Set(['US', 'CA', 'AU']);

export function AddressForm({
  initial,
  submitLabel = 'Save address',
  showDefaultToggle = false,
  defaultChecked = false,
  onSubmit,
  onCancel,
}: {
  initial?: ShippingAddress | null;
  submitLabel?: string;
  showDefaultToggle?: boolean;
  defaultChecked?: boolean;
  onSubmit: (addr: ShippingAddress, makeDefault: boolean) => Promise<void>;
  onCancel?: () => void;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [fullName, setFullName] = useState(initial?.full_name ?? '');
  const [street1, setStreet1] = useState(initial?.street1 ?? '');
  const [street2, setStreet2] = useState(initial?.street2 ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [postalCode, setPostalCode] = useState(initial?.postal_code ?? '');
  const [country, setCountry] = useState(initial?.country || 'US');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [makeDefault, setMakeDefault] = useState(defaultChecked);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stateRequired = STATE_REQUIRED.has(country);
  const valid =
    fullName.trim() !== '' && street1.trim() !== '' && city.trim() !== '' &&
    postalCode.trim() !== '' && country !== '' && (!stateRequired || state.trim() !== '');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        id: initial?.id,
        label: label.trim() === '' ? null : label.trim(),
        full_name: fullName.trim(),
        street1: street1.trim(),
        street2: street2.trim() === '' ? null : street2.trim(),
        city: city.trim(),
        state: state.trim() === '' ? null : state.trim(),
        postal_code: postalCode.trim(),
        country,
        phone: phone.trim() === '' ? null : phone.trim(),
      }, makeDefault);
    } catch (err: any) {
      setError(err?.message ?? 'Could not save your address. Try again.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="group">
        <Field label="Label" htmlFor="addr-label" optional>
          <Input id="addr-label" value={label} onChange={(e) => setLabel(e.target.value)}
                 maxLength={40} placeholder="Home, work…" disabled={busy} />
        </Field>
        <Field label="Full name" htmlFor="addr-name">
          <Input id="addr-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                 autoComplete="shipping name" disabled={busy} required />
        </Field>
        <Field label="Street address" htmlFor="addr-street1">
          <Input id="addr-street1" value={street1} onChange={(e) => setStreet1(e.target.value)}
                 autoComplete="shipping address-line1" disabled={busy} required />
        </Field>
        <Field label="Apt / unit" htmlFor="addr-street2" optional>
          <Input id="addr-street2" value={street2} onChange={(e) => setStreet2(e.target.value)}
                 autoComplete="shipping address-line2" disabled={busy} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="City" htmlFor="addr-city">
            <Input id="addr-city" value={city} onChange={(e) => setCity(e.target.value)}
                   autoComplete="shipping address-level2" disabled={busy} required />
          </Field>
          <Field label={stateRequired ? 'State' : 'State / region'} htmlFor="addr-state" optional={!stateRequired}>
            <Input id="addr-state" value={state} onChange={(e) => setState(e.target.value)}
                   autoComplete="shipping address-level1" disabled={busy} required={stateRequired} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Postcode / ZIP" htmlFor="addr-zip">
            <Input id="addr-zip" value={postalCode} onChange={(e) => setPostalCode(e.target.value)}
                   autoComplete="shipping postal-code" disabled={busy} required />
          </Field>
          <Field label="Country" htmlFor="addr-country">
            <Select id="addr-country" value={country} onChange={(e) => setCountry(e.target.value)}
                    autoComplete="shipping country" disabled={busy} required>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Phone" htmlFor="addr-phone" hint="Some carriers need this for delivery." optional>
          <Input id="addr-phone" type="tel" inputMode="tel" value={phone}
                 onChange={(e) => setPhone(e.target.value)} autoComplete="shipping tel" disabled={busy} />
        </Field>

        {showDefaultToggle && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)}
                   disabled={busy} />
            <Text role="small">Use as my default delivery address</Text>
          </label>
        )}

        {error && <Text role="small" className="text-danger" aria-live="polite">{error}</Text>}

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          )}
          <Button type="submit" variant="primary" size="md" loading={busy} disabled={!valid}>
            {submitLabel}
          </Button>
        </div>
      </Stack>
    </form>
  );
}
